import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import PageShell from "@/components/PageShell";
import { api, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Calendar, Download, Lock, ArrowLeft, Crown, Flame, Edit2, MessageSquare } from "lucide-react";
import { TIERS } from "@/lib/tiers";
import { sanitizeHtml } from "@/lib/sanitize";
import ReactionBar from "@/components/ReactionBar";
import Comments from "@/components/Comments";
import MetaBadges from "@/components/MetaBadges";
import RequestedByBadge from "@/components/RequestedByBadge";

export default function PostDetailPage() {
  const { slug } = useParams();
  const { user } = useAuth();
  const [post, setPost] = useState(null);
  const [err, setErr] = useState("");
  const [activeShot, setActiveShot] = useState(0);
  const [lightbox, setLightbox] = useState(null);
  const [showAllTags, setShowAllTags] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/posts/${slug}`);
        if (!cancelled) setPost(data);
      } catch (e) {
        console.error("Failed to load post:", e);
        if (!cancelled) setErr("Post not found");
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  if (err) return <PageShell><div className="text-center py-32 text-zinc-400">{err}</div></PageShell>;
  if (!post) return <PageShell><div className="text-center py-32 font-mystic text-zinc-500">Unsealing the scroll…</div></PageShell>;

  const tier = TIERS[post.access_level] || TIERS.public;
  const locked = post._locked;
  const requiredTier = post.access_level;

  return (
    <PageShell>
      {/* Cover */}
      <section className="relative">
        <div className="absolute inset-0 -z-10">
          {post.thumbnail_url ? (
            <img src={fileUrl(post.thumbnail_url)} alt="" className="w-full h-[60vh] object-cover opacity-40" />
          ) : (
            <div className="w-full h-[60vh] bg-gradient-to-br from-rose-900/40 to-black" />
          )}
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/70 to-[#050505]" />
        </div>
        <div className="max-w-7xl mx-auto px-6 pt-10 pb-8 relative">
          <Link to="/posts" className="inline-flex items-center text-sm text-zinc-400 hover:text-rose-300" data-testid="back-to-posts">
            <ArrowLeft className="w-4 h-4 mr-1.5" /> Back to library
          </Link>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 -mt-2 pb-20 relative">
        <div className="grid lg:grid-cols-12 gap-8">
          {/* Main */}
          <div className="lg:col-span-8">
            <div className="flex items-center gap-2 text-xs">
              <span className={`px-2 py-0.5 rounded ${tier.chip} uppercase tracking-wider`}>{tier.label}</span>
              {post.featured && <span className="px-2 py-0.5 rounded bg-rose-600/30 border border-rose-500/40 text-rose-200 uppercase tracking-wider">Featured</span>}
              {post.status === "draft" && <span className="px-2 py-0.5 rounded bg-amber-700/30 border border-amber-500/40 text-amber-200">Draft</span>}
            </div>
            {/* Engine + Status badges directly below access level */}
            <div className="mt-2">
              <MetaBadges engine={post.engine} dev_status={post.dev_status} size="md" />
            </div>
            <h1 className="font-display text-4xl sm:text-5xl font-black text-white tracking-tighter mt-3">{post.title}</h1>
            {/* "Requested by" supporter credit — sits between title and Version/Date.
                Renders nothing when post.requested_by_user is empty/null. */}
            {post.requested_by_user && <RequestedByBadge user={post.requested_by_user} size="md" />}
            <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-1.5 text-sm text-zinc-400">
              {post.version && <span><span className="text-zinc-500">Version:</span> <span className="text-white font-semibold">v{post.version}</span></span>}
              {post.release_date && <span className="inline-flex items-center gap-1"><Calendar className="w-3.5 h-3.5" />{post.release_date}</span>}
            </div>

            {/* Cover thumbnail */}
            {locked && (
              <div className="mt-7 relative aspect-video rounded-xl overflow-hidden border border-white/10 bg-zinc-900" data-testid="detail-locked-banner">
                {post.thumbnail_url ? (
                  <img src={fileUrl(post.thumbnail_url)} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-zinc-900 via-rose-950/30 to-black" />
                )}
                <div className="absolute inset-0 bg-black/45" />
                <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
                  <div className="w-14 h-14 rounded-full bg-rose-600/25 border border-rose-500/50 flex items-center justify-center mb-3 shadow-lg shadow-rose-900/50">
                    <Lock className="w-7 h-7 text-rose-200" />
                  </div>
                  <div className="text-[11px] uppercase tracking-widest text-rose-300">Locked Content</div>
                  <div className="mt-1 font-display text-2xl font-black text-white">{tier.label} Required</div>
                </div>
              </div>
            )}

            {/* Gallery — hidden when locked */}
            {!locked && (post.screenshots?.length || 0) > 0 && (
              <div className="mt-7">
                <div className="aspect-video rounded-xl overflow-hidden bg-zinc-900 border border-white/10 cursor-pointer" onClick={() => setLightbox(post.screenshots[activeShot])}>
                  <img src={fileUrl(post.screenshots[activeShot])} alt="Screenshot" className="w-full h-full object-cover" />
                </div>
                <div className="mt-3 flex gap-2 overflow-x-auto pb-2">
                  {post.screenshots.map((s, i) => (
                    <button key={s} onClick={() => setActiveShot(i)} className={`flex-shrink-0 w-24 h-16 rounded-md overflow-hidden border-2 ${activeShot === i ? "border-rose-500" : "border-white/10"}`} data-testid={`screenshot-thumb-${i}`}>
                      <img src={fileUrl(s)} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Locked → membership upsell panel. Description, changelog,
                reactions, and comments are intentionally not rendered. */}
            {locked ? (
              <div
                data-testid="gated-panel"
                className="mt-8 glass-strong rounded-2xl p-8 border border-rose-500/30 relative overflow-hidden"
              >
                <div className="absolute inset-0 pointer-events-none bg-gradient-to-br from-rose-900/15 via-transparent to-rose-900/10" />
                <div className="relative">
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-rose-600/15 border border-rose-500/40 text-rose-200 text-[11px] uppercase tracking-widest">
                    <Lock className="w-3.5 h-3.5" /> Membership Required
                  </div>
                  <h2 className="mt-4 font-display text-2xl sm:text-3xl font-black text-white">
                    This release requires {tier.label} membership.
                  </h2>
                  <p className="mt-3 text-zinc-300 max-w-2xl">
                    Upgrade your membership to unlock the full description, screenshots,
                    download links, reactions, and comments for this release.
                  </p>
                  <div className="mt-6 flex flex-wrap gap-3">
                    <Link to="/membership" data-testid="gated-upgrade-cta">
                      <Button className="bg-rose-600 hover:bg-rose-500 font-semibold">
                        {requiredTier === "hardcore_cultist" ? <Flame className="w-4 h-4 mr-2" /> : <Crown className="w-4 h-4 mr-2" />}
                        Upgrade to {tier.label}
                      </Button>
                    </Link>
                    {!user && (
                      <Link to="/login" data-testid="gated-login-cta">
                        <Button variant="outline" className="border-white/15 text-zinc-200 hover:bg-white/5">Sign in</Button>
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <>
                {/* Description */}
                <div className="mt-10">
                  <h2 className="font-display text-xl font-bold text-white mb-3">Description</h2>
                  <div className="prose-mystic max-w-none" dangerouslySetInnerHTML={{ __html: sanitizeHtml(post.description) || "<p class='text-zinc-500'>No description yet.</p>" }} />
                </div>

                {/* Changelog */}
                {post.changelog && (
                  <div className="mt-10">
                    <h2 className="font-display text-xl font-bold text-white mb-3">Changelog</h2>
                    <pre className="text-sm text-zinc-300 bg-black/40 border border-white/10 rounded-lg p-5 whitespace-pre-wrap font-body">{post.changelog}</pre>
                  </div>
                )}

                {/* Reactions */}
                <div className="mt-8 flex flex-wrap items-center justify-between gap-4 pt-6 border-t border-white/10">
                  <ReactionBar targetType="post" targetId={post.id} initial={post.reactions || {}} />
                  <div className="text-sm text-zinc-400 inline-flex items-center gap-1.5">
                    <MessageSquare className="w-4 h-4" /> {post.comment_count || 0} comment{(post.comment_count || 0) === 1 ? "" : "s"}
                  </div>
                </div>

                {/* Comments */}
                <Comments postSlug={post.slug} />
              </>
            )}
          </div>

          {/* Sidebar */}
          <aside className="lg:col-span-4 space-y-5">
            {user?.role === "admin" && (
              <Link to={`/admin/posts/${post.id}/edit`} data-testid="admin-edit-post">
                <Button variant="outline" className="w-full border-amber-500/40 text-amber-300 hover:bg-amber-900/20"><Edit2 className="w-4 h-4 mr-2" />Edit Post (Admin)</Button>
              </Link>
            )}
            <div className="glass-strong rounded-xl p-6 relative overflow-hidden">
              <h3 className="font-display text-lg font-bold text-white mb-4 flex items-center gap-2">
                <Download className="w-4 h-4 text-rose-400" /> Download Links
              </h3>
              {locked ? (
                <div className="text-center py-10">
                  <Lock className="w-10 h-10 text-rose-400 mx-auto mb-3" />
                  <div className="font-display text-base font-semibold text-white">Access Restricted</div>
                  <p className="text-sm text-zinc-400 mt-2 font-mystic italic">
                    Requires {TIERS[requiredTier]?.label} or higher.
                  </p>
                  <Link to="/membership" data-testid="upgrade-cta">
                    <Button className="mt-5 w-full bg-rose-600 hover:bg-rose-500 font-semibold">
                      {requiredTier === "hardcore_cultist" ? <Flame className="w-4 h-4 mr-2" /> : <Crown className="w-4 h-4 mr-2" />}
                      Upgrade Membership
                    </Button>
                  </Link>
                </div>
              ) : (post.download_links || []).length === 0 ? (
                <div className="text-sm text-zinc-500 text-center py-6">No download links available yet.</div>
              ) : (
                <ul className="space-y-2">
                  {(post.download_links || []).map((dl, i) => {
                    const href = dl.signed_url ? fileUrl(dl.signed_url) : dl.url;
                    const k = `${dl.label}-${dl.url || dl.file_id || i}`;
                    return (
                      <li key={k}>
                        <a
                          href={href}
                          target="_blank"
                          rel="noreferrer"
                          className="block p-3 rounded-lg bg-black/40 border border-white/10 hover:border-rose-500/40 hover:bg-rose-950/20 transition"
                          data-testid={`download-link-${i}`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="font-display font-semibold text-white text-sm">{dl.label || "Download"}</div>
                            <Download className="w-4 h-4 text-rose-400" />
                          </div>
                          {dl.note && <div className="text-xs text-zinc-400 mt-1">{dl.note}</div>}
                        </a>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            {(post.tags || []).length > 0 && (
              <div className="glass rounded-xl p-5">
                <div className="text-xs uppercase tracking-widest text-zinc-500 mb-2">Tags</div>
                <div className="flex flex-wrap gap-1.5">
                  {(showAllTags ? post.tags : post.tags.slice(0, 5)).map((t) => (
                    <span key={t} className="px-2 py-1 rounded text-xs bg-white/5 border border-white/10 text-zinc-300">{t}</span>
                  ))}
                  {post.tags.length > 5 && (
                    <button
                      type="button"
                      onClick={() => setShowAllTags((v) => !v)}
                      data-testid="detail-tags-toggle"
                      className="px-2 py-1 rounded text-xs bg-rose-900/30 border border-rose-500/30 text-rose-200 hover:bg-rose-800/40 transition"
                    >
                      {showAllTags ? "Show Less ▲" : `Show More ▼ (${post.tags.length - 5})`}
                    </button>
                  )}
                </div>
              </div>
            )}
          </aside>
        </div>
      </section>

      {lightbox && (
        <div className="fixed inset-0 bg-black/90 z-[100] grid place-items-center p-6 cursor-pointer" onClick={() => setLightbox(null)} data-testid="lightbox-overlay">
          <img src={fileUrl(lightbox)} alt="" className="max-w-full max-h-full rounded-lg" />
        </div>
      )}
    </PageShell>
  );
}